import axios from "axios";
import React, { useEffect, useMemo } from "react";
import { addIdToProjectItems, makeUnique } from "../helperJs/helper";
import {
  GET_LLM_LATEST_DATA,
  GET_LLM_OLD_DATA,
  GET_LLM_TRACKER_PROJECT,
} from "../services/constants";
import { LLMTrackerStore } from "../store/LLMTrackerStore";

const GetGuestLLMTrackerData = ({ email, websiteurl }) => {
  const {
    USER_LLM_PROJECT,
    CURRLOCATION,
    SELECTED_PROJECT,
    KEYWORD_LOC_DATA,
    KEYWORD_OLD_DATA,
    KEYWORD_LATEST_DATA,
    KEYWORD_ALL_LOC_DATA,
    OLD_DATA_DATE,
  } = LLMTrackerStore((state) => state);

  const projectsByLoc = useMemo(() => {
    if (USER_LLM_PROJECT?.length === 0 || !SELECTED_PROJECT) return [];
    const selectedProjectAllLoc = USER_LLM_PROJECT?.filter(
      (project) =>
        project.weburl === SELECTED_PROJECT.weburl &&
        project.language_code === SELECTED_PROJECT.language_code
    );

    return CURRLOCATION.name === "All Locations"
      ? selectedProjectAllLoc
      : selectedProjectAllLoc?.filter(
          (project) => project.location_name === CURRLOCATION.name
        );
  }, [CURRLOCATION, SELECTED_PROJECT, USER_LLM_PROJECT]);

  const showLoaderWithDelay = () => {
    LLMTrackerStore.setState({
      SHOWLOADER: false,
    });
  };

  const getUserProject = async () => {
    if (email == null) return;
    try {
      const response = await axios.get(GET_LLM_TRACKER_PROJECT(email));
      const { data } = response;
      if (Array.isArray(data?.data) && data?.data.length > 0) {
        const userProjects = addIdToProjectItems(data.data);
        LLMTrackerStore.setState(() => ({
          USER_LLM_PROJECT: userProjects,
          ISPROJECT: true,
        }));
      } else {
        sessionStorage.removeItem("LAST_LLMPRPJECT_SELECTED");
        LLMTrackerStore.setState({
          ISPROJECT: false,
          SHOWLOADER: false,
        });
      }
    } catch (error) {
      console.error("Error fetching user LLM project:", error);
      sessionStorage.removeItem("LAST_LLMPRPJECT_SELECTED");
      LLMTrackerStore.setState({
        ISPROJECT: false,
        SHOWLOADER: false,
      });
    }
  };

  const handleProjectSelection = () => {
    if (websiteurl) {
      const selectedProject = USER_LLM_PROJECT?.find(
        (project) => `${project.weburl}-${project.language_code}` === websiteurl
      );
      LLMTrackerStore.setState({
        SELECTED_PROJECT: selectedProject,
        SHOWLOADER: false,
      });
    }
  };

  const convertToKeywordData = (keywords = [], location_code) => {
    return keywords.map((kw) => ({
      keyword: kw,
      location: location_code,
      type: "pending",
    }));
  };

  const handlePreviousDataCall = async () => {
    if (projectsByLoc?.length === 0 || !SELECTED_PROJECT) return;
    const getCountry = projectsByLoc
      ?.map((item) => {
        if (item.keyword?.length === 0) return null;
        return {
          location_name: item.location_name,
          location_code: item.location_code,
        };
      })
      .filter(Boolean);
    let uniqueCountry = makeUnique(getCountry);

    const oldDataPromises = uniqueCountry?.map(async (location) => {
      const oldDataPayload = {
        ...SELECTED_PROJECT,
        location_code: location?.location_code, // country code
        location_name: location?.location_name, // country name
        days: OLD_DATA_DATE?.day,
      };
      // return a Promise that also remembers its location
      try {
        const response = await axios.get(GET_LLM_OLD_DATA(oldDataPayload));
        const value = {
          data: response.data,
          location: location.location_name,
        };

        return { status: "fulfilled", value, location };
      } catch (err) {
        return { status: "rejected", reason: err, location };
      }
    });

    const oldResults = await Promise.all(oldDataPromises);
    const successfulOldData = oldResults
      .filter((res) => res.status === "fulfilled")
      .map((res) => res.value);

    if (successfulOldData) {
      LLMTrackerStore.setState({
        KEYWORD_OLD_DATA: successfulOldData,
      });
    }
  };

  const handleApiCall = async () => {
    if (projectsByLoc?.length === 0) return;
    const getCountry = projectsByLoc
      ?.map((item) => {
        if (item.keyword?.length === 0) return null;
        return {
          location_name: item.location_name,
          location_code: item.location_code,
        };
      })
      .filter(Boolean);
    let uniqueCountry = makeUnique(getCountry);
    const latestDataPromises = uniqueCountry.map(async (location) => {
      const latestDataPayload = {
        ...SELECTED_PROJECT,
        location_code: location?.location_code, // country code
        location_name: location?.location_name, // country name
      };
      const project = projectsByLoc?.find(
        (project) => project.location_name === location.location_name
      );
      const keywordArr = [
        {
          data: convertToKeywordData(project.keyword, location.location_code),
        },
      ];
      // return a Promise that also remembers its location
      try {
        const response = await axios.get(
          GET_LLM_LATEST_DATA(latestDataPayload)
        );
        const data = response?.data?.length === 0 ? keywordArr : response.data;
        const value = { data, location: location.location_name };
        return { status: "fulfilled", value, location };
      } catch (err) {
        return { status: "rejected", reason: err, location };
      }
    });

    const latestResults = await Promise.all(latestDataPromises);
    // Extract successful data if you want
    const successfulLatestData = latestResults
      .filter((res) => res.status === "fulfilled")
      .map((res) => res.value);

    if (successfulLatestData) {
      LLMTrackerStore.setState({
        KEYWORD_LATEST_DATA: successfulLatestData,
      });
    }
    showLoaderWithDelay();
    handlePreviousDataCall();
  };

  const handleProjectData = async () => {
    if (projectsByLoc?.length === 0) return;
    if (
      CURRLOCATION.name === KEYWORD_LOC_DATA &&
      KEYWORD_OLD_DATA?.length > 0 &&
      KEYWORD_LATEST_DATA?.length > 0
    ) {
      LLMTrackerStore.setState({
        KEYWORD_OLD_DATA: KEYWORD_OLD_DATA,
        KEYWORD_LATEST_DATA: KEYWORD_LATEST_DATA,
        KEYWORD_LOC_DATA: KEYWORD_LOC_DATA,
        SHOWLOADER: false,
      });
    } else if (
      CURRLOCATION.name === "All Locations" &&
      KEYWORD_ALL_LOC_DATA?.length === 0
    ) {
      LLMTrackerStore.setState({
        KEYWORD_LOC_DATA: "All Locations",
      });
      handleApiCall();
    } else if (KEYWORD_ALL_LOC_DATA?.length > 0) {
      const data = KEYWORD_ALL_LOC_DATA?.[0];
      if (CURRLOCATION.name === "All Locations") {
        LLMTrackerStore.setState({
          KEYWORD_OLD_DATA: data?.KEYWORD_OLD_DATA,
          KEYWORD_LATEST_DATA: data?.KEYWORD_LATEST_DATA,
          KEYWORD_LOC_DATA: CURRLOCATION.name,
        });
      } else {
        const old_data = data?.KEYWORD_OLD_DATA?.filter((item) => {
          return item.location === CURRLOCATION.name;
        });
        const new_data = data?.KEYWORD_LATEST_DATA?.filter((item) => {
          return item.location === CURRLOCATION.name;
        });
        LLMTrackerStore.setState({
          KEYWORD_OLD_DATA: old_data,
          KEYWORD_LATEST_DATA: new_data,
          KEYWORD_LOC_DATA: CURRLOCATION.name,
        });
      }
      showLoaderWithDelay();
    } else {
      LLMTrackerStore.setState({
        KEYWORD_LOC_DATA: CURRLOCATION.name,
        KEYWORD_OLD_DATA: [],
        KEYWORD_LATEST_DATA: [],
      });
      handleApiCall();
    }
  };

  useEffect(() => {
    if (
      KEYWORD_LOC_DATA === "All Locations" &&
      KEYWORD_ALL_LOC_DATA?.length === 0 &&
      KEYWORD_OLD_DATA?.length > 0 &&
      KEYWORD_LATEST_DATA?.length > 0
    ) {
      LLMTrackerStore.setState({
        KEYWORD_ALL_LOC_DATA: [
          {
            KEYWORD_OLD_DATA: KEYWORD_OLD_DATA,
            KEYWORD_LATEST_DATA: KEYWORD_LATEST_DATA,
          },
        ],
      });
    }
  }, [
    KEYWORD_LOC_DATA,
    KEYWORD_OLD_DATA,
    KEYWORD_LATEST_DATA,
    KEYWORD_ALL_LOC_DATA,
  ]);

  useEffect(() => {
    handleProjectData();
  }, [projectsByLoc]);

  useEffect(() => {
    if (USER_LLM_PROJECT?.length > 0) {
      handleProjectSelection(); // need to update on refresh, add proj and delete proj
    } else {
      getUserProject();
    }
  }, [USER_LLM_PROJECT, email, SELECTED_PROJECT, websiteurl]);

  useEffect(() => {
    if (OLD_DATA_DATE.refresh) {
      handlePreviousDataCall();
    }
  }, [OLD_DATA_DATE]);

  return null;
};

export default GetGuestLLMTrackerData;
